import SinglePageView from './singlepageview.js';
import { EVENTS } from 'epubjs/src/utils/constants';
import VIEW_SLIDING_STATE from './viewslidingstate.js';

class SlidingIframeView extends SinglePageView {
  constructor(section, options) {
    super(section, options);
    this.slidingState = null;
    this.isPreCentered = false;
    this.isRTL = options.direction === 'rtl';
  }

  /**
   * Get the body element from the iframe
   */
  getBodyElement() {
    if (!this.contents) return null;
    const doc = this.contents.document;
    return doc ? doc.body : null;
  }

  /**
   * Check if view is currently shown
   */
  isShown() {
    return this.element && this.element.style.visibility === 'visible';
  }

  render(request, show) {
    return super.render(request, show).then(() => {
      // css reset
      if (this.element) {
        Object.assign(this.element.style, {
          position: 'absolute',
          top: '0',
          left: '0',
          width: '100%',
          height: '100%',
          margin: '0',
          padding: '0',
          border: 'none',
          overflow: 'hidden',
          willChange: 'transform',
        });
      }

      if (this.iframe) {
        Object.assign(this.iframe.style, {
          width: '100%',
          height: '100%',
          margin: '0',
          padding: '0',
          border: 'none',
          visibility: 'visible',
        });
      }

      // Initial Positioning
      this.updateState(this.slidingState || VIEW_SLIDING_STATE.CURRENT_PAGE);
    });
  }

  updateState(state) {
    this.slidingState = state;
    if (!this.element) {
      return;
    }

    // Remove previous state classes
    this.element.classList.remove(
      VIEW_SLIDING_STATE.PRELOADED_PREV,
      VIEW_SLIDING_STATE.CURRENT_PAGE,
      VIEW_SLIDING_STATE.PRELOADED_NEXT,
    );

    // Add current state class
    if (state) {
      this.element.classList.add(state);
    }

    let xPos = 0;
    let isVisible = true;

    switch (state) {
      case VIEW_SLIDING_STATE.PRELOADED_PREV:
        xPos = this.isRTL ? 100 : -100;
        break;
      case VIEW_SLIDING_STATE.CURRENT_PAGE:
        xPos = 0;
        break;
      case VIEW_SLIDING_STATE.PRELOADED_NEXT:
        xPos = this.isRTL ? -100 : 100;
        break;
      default:
        isVisible = false;
    }

    this.element.style.transform = `translate3d(${xPos}%, 0, 0)`;

    // Manage Visibility
    const visibility = isVisible ? 'visible' : 'hidden';
    this.element.style.visibility = visibility;
    if (this.iframe) {
      this.iframe.style.visibility = visibility;
    }

    // Force "SHOWN" event for Current Page to sync epub.js location
    // Skip if already pre-centered to avoid re-centering jump
    if (state === VIEW_SLIDING_STATE.CURRENT_PAGE) {
      if (!this.isPreCentered) {
        this.emit(EVENTS.VIEWS.SHOWN, this);
      }
      // If pre-centered but margin was somehow cleared, recalculate
      else if (this.contents?.document?.body) {
        const currentMargin = this.contents.document.body.style.marginLeft;
        if (!currentMargin || currentMargin === '0px' || currentMargin === '') {
          this.emit(EVENTS.VIEWS.SHOWN, this);
        }
      }
    }
  }

  show() {
    this.updateState(this.slidingState);
  }

  /**
   * Pre-center content for preloaded views (prev/next)
   * Copy the positioning and layout from the current view to avoid alignment jump after animation
   */
  preCenterContent(currentView) {
    if (!this.displayed || !this.element || !this.contents) {
      return;
    }

    // Mark as pre-centered to skip SHOWN event when becoming current
    this.isPreCentered = true;

    // Copy iframe body margin styles from current view
    if (currentView && currentView.contents && currentView.contents.document) {
      const currentBody = currentView.contents.document.body;
      const thisBody = this.contents.document.body;

      if (currentBody && thisBody) {
        // Get computed styles (not just inline styles)
        const currentWindow = currentView.contents.window;
        const computedStyle = currentWindow.getComputedStyle(currentBody);

        // Copy margins as inline styles to preserve them
        thisBody.style.marginLeft = computedStyle.marginLeft;
        thisBody.style.marginRight = computedStyle.marginRight;
      }
    }
  }
}

export default SlidingIframeView;
